import { Callout } from '__types__/v1/models/Callout';
import store from '../../../../store';
import { CALLOUT_ACTIONS } from '../../../../store/v1/callouts/callouts.constants.js';
import { apiClient } from 'v4/core/apiClient';
import {
  callout$,
  calloutFromHttp
} from 'v5/api/callouts/callouts.http.schema';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { cloneCalloutMappings } from 'v5/features/callouts/callout-templates';
import { SystemEffect } from '../../internal/MessageService/_private/SystemEffectMessage';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import {
  CalloutMutationService,
  CreateCalloutInput,
  DuplicateCalloutInput,
  UpdateCalloutInput
} from './CalloutMutationService';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';

export function createCalloutMutationService(
  config: WithServices<[WithErrorService, WithMessageService]>
): CalloutMutationService {
  const {
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('CalloutMutationService');

  async function reportEffectAndWaitUntilCacheReady(
    scope: SystemEffect[] = []
  ) {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope
      },
      3000
    );
  }

  const service: CalloutMutationService = {
    createCallout: async (request: CreateCalloutInput) => {
      const result = await interceptor.wrap(async () => {
        const response = await apiClient.post('/callouts', {
          data: request,
          token: true
        });
        return calloutFromHttp(parseWithDecoratedError(callout$, response));
      })();

      store.dispatch({
        type: CALLOUT_ACTIONS.CREATE_CALLOUT_SUCCESS,
        result
      });

      await reportEffectAndWaitUntilCacheReady([
        {
          entity: 'callout',
          entityId: result.id,
          payload: { type: 'create' }
        }
      ]);

      return result;
    },

    duplicateCallout: async (request: DuplicateCalloutInput) => {
      const { sourceCallout, title, slug, resource_type_id } = request;

      return service.createCallout({
        title,
        slug,
        resource_type_id,
        enterprise_workspace_id: request.enterprise_workspace_id,
        mappings: sourceCallout.mappings?.length
          ? cloneCalloutMappings(sourceCallout.mappings)
          : [],
        metadata: {
          ...sourceCallout.metadata,
          useNewForm: sourceCallout.metadata?.useNewForm ?? true
        }
      });
    },

    updateCallout: async (id: Callout['id'], request: UpdateCalloutInput) => {
      const result = await interceptor.wrap(async () => {
        const response = await apiClient.put(`/callouts/${id}`, {
          data: request,
          token: true
        });
        return calloutFromHttp(parseWithDecoratedError(callout$, response));
      })();

      store.dispatch({
        type: CALLOUT_ACTIONS.UPDATE_CALLOUT_SUCCESS,
        result
      });

      await reportEffectAndWaitUntilCacheReady([
        {
          entity: 'callout',
          entityId: result.id,
          payload: { type: 'update' }
        }
      ]);

      return result;
    },

    transferCalloutsToWorkspace: async request => {
      await interceptor.wrap(async () => {
        await apiClient.post('/batch/callouts/transfer', {
          data: request,
          token: true
        });
      })();

      await reportEffectAndWaitUntilCacheReady(
        request.ids.map(id => ({
          entity: 'callout' as const,
          entityId: id,
          payload: { type: 'update' as const }
        }))
      );
    }
  };

  return service;
}
